import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { SettingsHeader } from "../";
import {
  useCreateBillingPortalSessionMutation,
  useCreateCheckoutSessionMutation,
  useGetBillingSummaryQuery,
} from "../../../redux/services/billing/billingApi";
import { BILLING_INVOICE_FETCH_LIMIT } from "./billing.utils";
import { BillingCurrentPlan } from "./BillingCurrentPlan";
import { BillingInvoiceHistory } from "./BillingInvoiceHistory";
import { BillingPaymentMethod } from "./BillingPaymentMethod";
import { BillingPlanOptions } from "./BillingPlanOptions";

export const Billing = () => {
  const {
    data: billingSummaryData,
    isLoading: billingLoading,
    refetch: refetchBillingSummary,
  } = useGetBillingSummaryQuery({ limit: BILLING_INVOICE_FETCH_LIMIT });
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Stripe Checkout return handler.
   */
  useEffect(() => {
    const checkoutResult = searchParams.get("checkout");
    if (!checkoutResult) return;

    if (checkoutResult === "success") {
      toast.success(
        "Subscription started. It can take a few seconds for billing details to appear here."
      );
      refetchBillingSummary();
    } else if (checkoutResult === "cancel") {
      toast.info("Checkout was cancelled. No changes were made to your billing.");
    }

    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refetchBillingSummary]);

  const [createPortalSession, { isLoading: portalLoading }] =
    useCreateBillingPortalSessionMutation();
  const [createCheckoutSession, { isLoading: checkoutLoading }] =
    useCreateCheckoutSessionMutation();

  const billingSummary = billingSummaryData?.billing || {};
  const billingAccount = billingSummary?.billingAccount || {};
  const entitlements = billingSummary?.entitlements || {};
  const offer = billingSummary?.offer || null;
  const catalog = billingSummary?.catalog || {};
  const subscription = billingSummary?.subscription || null;
  const paymentMethods = billingSummary?.paymentMethods || [];
  const invoices = billingSummary?.invoices || [];
  const actions = billingSummary?.actions || {};
  const selfServeOffers = catalog?.selfServeOffers || [];
  const currentPlanKey = billingAccount?.planKey || offer?.key || null;
  const currentOffer =
    offer || (catalog?.offers || []).find((item) => item.key === currentPlanKey) || null;
  const selectedPaymentMethod = paymentMethods[0];
  const collectionMethod =
    billingAccount?.collectionMethod || "manual_invoice";
  const canManagePortal = Boolean(actions?.canManagePortal);
  const canStartCheckout = Boolean(actions?.canStartCheckout);

  const cycle =
    subscription?.items?.data?.[0]?.price?.recurring?.interval || offer?.interval || "month";
  const rawAmount = subscription?.items?.data?.[0]?.price?.unit_amount;
  const planCost = typeof rawAmount === "number" ? rawAmount : null;
  const planName =
    billingAccount?.planName ||
    currentOffer?.name ||
    (collectionMethod === "manual_invoice" ? "Contract plan" : "Custom plan");
  const nextBillingDate =
    billingAccount?.currentPeriodEnd ||
    (subscription?.current_period_end
      ? subscription.current_period_end * 1000
      : null);

  const openBillingPortal = async () => {
    try {
      const returnUrl = `${window.location.origin}/settings/billing`;
      const res = await createPortalSession({ returnUrl }).unwrap();
      if (res?.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Could not open billing portal. Please try again.");
      }
    } catch (err) {
      console.error("Failed to open billing portal:", err);
      toast.error(
        err?.data?.message ||
          "Failed to open billing portal. Please try again or contact support."
      );
    }
  };

  const startCheckout = async (planKey) => {
    try {
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/settings/billing?checkout=success`;
      const cancelUrl = `${baseUrl}/settings/billing?checkout=cancel`;
      const res = await createCheckoutSession({
        planKey:
          planKey || actions?.defaultCheckoutPlanKey || catalog?.defaultCheckoutPlanKey,
        successUrl,
        cancelUrl,
      }).unwrap();

      if (res?.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err) {
      console.error("Failed to create checkout session:", err);
      toast.error(
        err?.data?.message ||
          "Failed to start checkout. Please try again or contact support."
      );
    }
  };

  return (
    <>
      <SettingsHeader title="Billing" />
      <div className="mx-auto max-w-5xl space-y-6">
        <p className="text-sm text-gray-600">
          See your plan, payment method, and download invoices by month or year.
        </p>

        <BillingCurrentPlan
          loading={billingLoading}
          planName={planName}
          planCost={planCost}
          billingCycle={cycle}
          nextBillingDate={nextBillingDate}
          billingAccount={billingAccount}
          collectionMethod={collectionMethod}
          requiresBillingAttention={Boolean(entitlements?.requiresBillingAttention)}
          canManagePortal={canManagePortal}
          canStartCheckout={canStartCheckout}
          checkoutLoading={checkoutLoading}
          portalLoading={portalLoading}
          onStartCheckout={() => startCheckout()}
          onManageBilling={openBillingPortal}
        />

        <BillingPaymentMethod
          loading={billingLoading}
          collectionMethod={collectionMethod}
          selectedPaymentMethod={selectedPaymentMethod}
          canManagePortal={canManagePortal}
          portalLoading={portalLoading}
          onManageBilling={openBillingPortal}
        />

        <BillingInvoiceHistory
          invoices={invoices}
          loading={billingLoading}
          collectionMethod={collectionMethod}
          canManagePortal={canManagePortal}
        />

        <BillingPlanOptions
          collectionMethod={collectionMethod}
          selfServeOffers={selfServeOffers}
          currentPlanKey={currentPlanKey}
          canStartCheckout={canStartCheckout}
          canManagePortal={canManagePortal}
          checkoutLoading={checkoutLoading}
          portalLoading={portalLoading}
          onStartCheckout={startCheckout}
          onManageBilling={openBillingPortal}
        />
      </div>
    </>
  );
};
